// Admin global search — thin wrapper over the `admin_global_search` RPC.
//
// The RPC does all the work server-side in one round-trip: it ILIKE-matches
// across people / companies / competitions / inquiries / invites, caps each
// group, and is gated by `is_admin()` so a non-admin caller gets zero rows
// (nothing leaks that an admin couldn't already read). We only map the flat
// result list to display sections + navigation targets here.

import type { Href } from "expo-router";
import { supabase } from "./supabase";

export type AdminSearchKind =
  | "student"
  | "judge"
  | "user"
  | "company"
  | "competition"
  | "inquiry"
  | "invite";

export type AdminSearchRow = {
  kind: AdminSearchKind;
  id: string;
  title: string;
  subtitle: string;
  status: string | null;
};

/**
 * Run the global search. Returns [] for queries shorter than 2 chars without
 * hitting the network (the RPC short-circuits those too, but skipping the
 * round-trip keeps typing snappy).
 */
export async function adminGlobalSearch(
  query: string,
  perKind = 6,
): Promise<AdminSearchRow[]> {
  const q = query.trim();
  if (q.length < 2) return [];
  const { data, error } = await supabase.rpc("admin_global_search", {
    p_query: q,
    p_limit: perKind,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as AdminSearchRow[];
}

// Competition rows still in an approval state deep-link into the case-approvals
// inbox (which opens the matching panel from `?case=`); everything else lands
// on the competitions list.
const CASE_REVIEW_STATUSES = new Set(["submitted", "under_review"]);

/**
 * Where selecting a result takes you. People resolve to their detail screens
 * (the judge route uses the same auth user id); generic users land on the
 * users list for now — that's the seam where Fix 25's view-as flow plugs in.
 */
export function hrefForResult(row: AdminSearchRow): Href {
  switch (row.kind) {
    case "student":
      return `/admin/student/${row.id}` as Href;
    case "judge":
      return `/admin/judge/${row.id}` as Href;
    case "company":
      return `/admin/company/${row.id}` as Href;
    case "competition":
      return (
        row.status && CASE_REVIEW_STATUSES.has(row.status)
          ? `/admin/case-approvals?case=${row.id}`
          : "/admin/competitions"
      ) as Href;
    case "inquiry":
      return "/admin/inquiries" as Href;
    case "invite":
      return "/admin/invites" as Href;
    case "user":
    default:
      return "/admin/users" as Href;
  }
}

// Display sections, in render order. People (student/judge/user) collapse into
// one "People" group; the RPC already returns rows in this section order so a
// straight group-preserving pass keeps the flat keyboard index aligned.
export type AdminSearchSection = {
  key: string;
  label: string;
  kinds: AdminSearchKind[];
};

export const ADMIN_SEARCH_SECTIONS: AdminSearchSection[] = [
  { key: "people", label: "People", kinds: ["student", "judge", "user"] },
  { key: "companies", label: "Companies", kinds: ["company"] },
  { key: "competitions", label: "Cases & competitions", kinds: ["competition"] },
  { key: "leads", label: "Leads", kinds: ["inquiry"] },
  { key: "invites", label: "Invites", kinds: ["invite"] },
];

/** Group a flat result list into render sections, dropping empty ones. */
export function groupResults(
  rows: AdminSearchRow[],
): { section: AdminSearchSection; rows: AdminSearchRow[] }[] {
  return ADMIN_SEARCH_SECTIONS.map((section) => ({
    section,
    rows: rows.filter((r) => section.kinds.includes(r.kind)),
  })).filter((g) => g.rows.length > 0);
}
